import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { isPeerLedGroupCoaching } from '../../../utils/liveSessionDelivery';
import { useSearchParams } from 'react-router-dom';
import {
  Group,
  Button,
  Text,
  Stack,
  ThemeIcon,
  Center,
  Loader,
  Alert,
  Switch,
  Badge,
  Collapse,
  ActionIcon,
  Box,
} from '@mantine/core';
import {
  Users,
  User,
  CheckCircle,
  Send,
  Plus,
  ChevronDown,
  ChevronRight,
  Bell,
  Info,
} from 'react-feather';
import type { CohortWithDetails, ResourceCategory, Session } from '../../../types';
import { isCheckinOneOnOneType } from '../../../types';
import { formatDate } from '../../../utils/formatters';
import { participantsApi } from '../../../services/api/participants';
import { sessionsApi } from '../../../services/api/sessions';
import { ExportModal } from '../../../components/common';
import { resourcesApi } from '../../../services/api/resources';
import { undatedOneOnOneOpensDrawer } from './sessions/sessionsHelpers';
import { EditSessionModal } from './EditSessionModal';
import { FinalizeInvitesModal } from './FinalizeInvitesModal';
import { ParticipantDetailModal } from './ParticipantDetailModal';
import { cohortEventsApi } from '../../../services/api/cohortEvents';
import type { CohortEventWithDetails } from '../../../services/api/cohortEvents';
import {
  SessionsSubNav,
  CohortEventsSection,
  GroupCoachingSessionsSection,
  TopicSelectModal,
  AddHuddleModal,
  SessionDetailDrawer,
  SurveyResultsCallout,
  SurveyResultsModal,
  CoachFeedbackReviewModal,
  SendCohortEventInvitesModal,
  EditCohortEventDateTimeModal,
  ViewCohortEventRsvpsModal,
  CohortEventDetailDrawer,
  SessionGroupCard,
  NoGroupsZeroState,
  NoSessionsZeroState,
  FlatSessionList,
  SessionsCalendarView,
  useSessionsData,
  showsOneOnOneSection,
  enabledCheckinCount,
  useSessionModals,
  getDisplayStatus,
  OneOnOneCoachingSection,
  OneOnOneSessionDetailDrawer,
  GroupCoachingBySessionView,
  OneOnOneBySessionView,
  BulkTopicEditorModal,
  type SessionTabValue,
  type OneOnOneViewMode,
} from './sessions';
import { AddTimeSlotModal } from './AddTimeSlotModal';
import { AssignOneOnOneCoachModal } from './sessions/AssignOneOnOneCoachModal';
import { AnnounceModalityModal } from './sessions/AnnounceModalityModal';
import { AnnounceCohortModal } from './sessions/AnnounceCohortModal';
import { WorkshopSessionsSection } from './sessions/WorkshopSessionsSection';
import { UnattachedBookingsSection } from './sessions/UnattachedBookingsSection';
import { GroupBookingLinks } from '../../coach/cohort-detail/GroupBookingLinks';
import { useImpersonationStore } from '../../../stores/impersonation';
import { ModalityConfigPills } from './ModalityConfigPills';
import { workshopsApi, type WorkshopWithSession } from '../../../services/api/workshops';
import type { OneOnOneParticipant, OneOnOneSessionSlot, ModalityType } from '../../../types';
import { useEmailToggle } from '../../../hooks';

interface SessionsTabProps {
  cohort: CohortWithDetails;
  onSessionUpdate?: (session: Session) => void;
  onNavigateToParticipants?: () => void;
  onNavigateToGroups?: () => void;
  isActive?: boolean;
  refreshKey?: number;
  onAttendanceComplete?: () => void;
  autoOpenSessionId?: string;
  autoOpenSessionTab?: string;
  onSessionAutoOpenComplete?: () => void;
  autoOpenEventId?: string;
  onEventAutoOpenComplete?: () => void;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
}

export function SessionsTab({
  cohort,
  onSessionUpdate,
  onNavigateToParticipants,
  onNavigateToGroups,
  isActive,
  refreshKey,
  onAttendanceComplete,
  autoOpenSessionId,
  autoOpenSessionTab,
  onSessionAutoOpenComplete,
  autoOpenEventId,
  onEventAutoOpenComplete,
  onOpenFollowUps,
}: SessionsTabProps) {
  // MED-578: Feature flag gate for all E18 multi-modality features
  const multiModalityEnabled = useFeatureFlag('multi_modality');
  const isImpersonating = useImpersonationStore(s => s.isImpersonating);

  // MED-579: Email toggle gating for announce modals
  const { isEnabled: isEmailEnabled } = useEmailToggle(cohort.id);
  const isAnnouncementEmailDisabled = !isEmailEnabled('participants', 'announcement');

  // MED-778: Audience feature toggle gating
  const participantFeaturesOff = cohort.participant_features_enabled === false;

  // MED-1076: per-modality "Live Session Delivery" setting for this cohort's
  // group-coaching modality. Threaded into SessionDetailDrawer so the "Edit
  // Session Content" entry point is gated on it (alongside the global flag).
  const groupCoachingLiveSessionDeliveryEnabled =
    cohort.modalities?.find((m) => m.modality_type === 'group_coaching')
      ?.live_session_delivery_enabled ?? false;

  // MED-537/MED-488: Read URL params for deep-linking from health nudges
  const [searchParams] = useSearchParams();
  const groupIdFromUrl = searchParams.get('groupId');
  const coachNameFromUrl = searchParams.get('coachName');

  // UI state for tab/filter/view
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  // MED-1350: the 1:1 list or card choice lives here. A save reloads the tab
  // data, the loader unmounts the 1:1 section, and its own state would reset.
  const [oneOnOneViewMode, setOneOnOneViewMode] = useState<OneOnOneViewMode>('list');
  const [activeTab, setActiveTab] = useState<SessionTabValue>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [coachFilter, setCoachFilter] = useState<string[]>([]);

  // MED-702 AC4: "View by Date" / "View by Session" toggle persists during
  // navigation within the page but resets on hard reload. In-memory only.
  const [groupingMode, setGroupingMode] = useState<'date' | 'session'>('date');
  const [bulkTopicEditorOpened, setBulkTopicEditorOpened] = useState(false);
  const [bySessionRefreshKey, setBySessionRefreshKey] = useState(0);
  const bumpBySessionRefresh = useCallback(() => setBySessionRefreshKey((k) => k + 1), []);

  // MED-537: Sync groupId URL param to filter state (handles navigation from health nudges)
  useEffect(() => {
    if (groupIdFromUrl) {
      setGroupFilter([groupIdFromUrl]);
    }
  }, [groupIdFromUrl]);

  // MED-488: Sync coachName URL param to coach filter (handles navigation from coach dissatisfaction alerts)
  useEffect(() => {
    if (coachNameFromUrl) {
      setCoachFilter([decodeURIComponent(coachNameFromUrl)]);
    }
  }, [coachNameFromUrl]);

  // Resource categories for session detail drawer
  const [resourceCategories, setResourceCategories] = useState<ResourceCategory[]>([]);
  const fetchCategories = useCallback(async () => {
    try {
      const cats = await resourcesApi.getCategories();
      setResourceCategories(cats);
    } catch {
      // Silent fail — categories are optional for display
    }
  }, []);
  useEffect(() => {
    if (isActive) fetchCategories();
  }, [isActive, fetchCategories]);

  // Data hook
  const data = useSessionsData(cohort, activeTab, groupFilter, coachFilter, isActive, refreshKey);

  // Modal state hook
  const modals = useSessionModals();

  // MED-545: Wrap onOpenFollowUps to close session drawer first (prevents double-pane layering)
  const handleOpenFollowUps = useCallback(
    (options?: { sessionId?: string; participantId?: string }) => {
      modals.closeSessionDetailModal();
      onOpenFollowUps?.(options);
    },
    [modals, onOpenFollowUps],
  );

  // Track default tab for session detail drawer (set by auto-open from health risk)
  const [drawerDefaultTab, setDrawerDefaultTab] = useState<string | undefined>(undefined);

  // Auto-open session detail drawer when navigated from health risk
  useEffect(() => {
    if (autoOpenSessionId && !data.loading && data.allSessions.length > 0) {
      const session = data.allSessions.find((s) => s.id === autoOpenSessionId);
      if (session) {
        setDrawerDefaultTab(autoOpenSessionTab);
        modals.setSelectedSession(session);
        modals.openSessionDetailModal();
        onSessionAutoOpenComplete?.();
      }
    }
  }, [autoOpenSessionId, data.loading, data.allSessions]); // eslint-disable-line react-hooks/exhaustive-deps

  // MED-1348: open the cohort event drawer from the CM attendance reminder
  // email link. Events load inside CohortEventsSection, so fetch the list here.
  useEffect(() => {
    if (!autoOpenEventId || !isActive) return;
    let cancelled = false;
    cohortEventsApi
      .list(cohort.id)
      .then((events) => {
        const event = events.find((e) => e.id === autoOpenEventId);
        if (cancelled || !event) return;
        modals.setSelectedCohortEvent(event);
        modals.openEventAttendance();
        onEventAutoOpenComplete?.();
      })
      .catch(() => {
        // The event list failed to load; leave the page as it is.
      });
    return () => {
      cancelled = true;
    };
  }, [autoOpenEventId, isActive, cohort.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Coach feedback review modal state (MED-330)
  const [coachFeedbackModalOpen, setCoachFeedbackModalOpen] = useState(false);
  const [coachFeedbackSurveyType, setCoachFeedbackSurveyType] = useState<'midpoint' | 'endpoint'>('midpoint');

  const handleOpenCoachFeedback = (surveyType: 'midpoint' | 'endpoint') => {
    setCoachFeedbackSurveyType(surveyType);
    setCoachFeedbackModalOpen(true);
  };

  // E18-2: 1:1 coach assignment + add participants modals
  const [assignCoachParticipant, setAssignCoachParticipant] = useState<OneOnOneParticipant | null>(null);
  const handleAssignOneOnOneCoach = (participant: OneOnOneParticipant) => {
    setAssignCoachParticipant(participant);
  };

  // MED-564 E18-8: Workshop sessions state
  const [workshops, setWorkshops] = useState<WorkshopWithSession[]>([]);
  const [workshopsKey, setWorkshopsKey] = useState(0);

  const loadWorkshops = useCallback(async () => {
    if (!data.modalities.hasWorkshop) return;
    try {
      const result = await workshopsApi.list(cohort.id);
      setWorkshops(result);
    } catch {
      // Silent fail — empty state will show
    }
  }, [cohort.id, data.modalities.hasWorkshop]);

  useEffect(() => {
    if (isActive) loadWorkshops();
  }, [isActive, loadWorkshops, workshopsKey]);

  const handleWorkshopUpdated = useCallback(() => {
    setWorkshopsKey((k) => k + 1);
  }, []);

  const workshopPattern: 'sequential' | 'parallel' =
    cohort.modalities?.find((m) => m.modality_type === 'workshop')?.configuration?.pattern ?? 'parallel';

  // MED-563 E18-5: Per-modality announce state
  const [announceModalityId, setAnnounceModalityId] = useState<string | null>(null);
  const [announceModalityType, setAnnounceModalityType] = useState<ModalityType | null>(null);
  const [locallyAnnouncedIds, setLocallyAnnouncedIds] = useState<Set<string>>(new Set());

  // MED-604: Cohort-level announce modal state
  const [cohortAnnounceModalOpened, setCohortAnnounceModalOpened] = useState(false);

  const handleOpenAnnounceModal = (modalityId: string, modalityType: ModalityType) => {
    setAnnounceModalityId(modalityId);
    setAnnounceModalityType(modalityType);
  };

  const handleAnnounceSuccess = (modalityId: string) => {
    setLocallyAnnouncedIds((prev) => new Set([...prev, modalityId]));
    setAnnounceModalityId(null);
    setAnnounceModalityType(null);
  };

  const handleCohortAnnounceSuccess = (announcedIds: string[]) => {
    setLocallyAnnouncedIds((prev) => new Set([...prev, ...announcedIds]));
    setCohortAnnounceModalOpened(false);
  };

  // E18-3: 1:1 session detail drawer + edit modal state
  const [selectedOneOnOneParticipant, setSelectedOneOnOneParticipant] = useState<OneOnOneParticipant | null>(null);
  const [selectedOneOnOneSessionId, setSelectedOneOnOneSessionId] = useState<string | null>(null);
  const [oneOnOneDetailOpened, setOneOnOneDetailOpened] = useState(false);
  const [editOneOnOneSession, setEditOneOnOneSession] = useState<{ session: Session; participant: OneOnOneParticipant } | null>(null);

  const handleOneOnOneSessionClick = useCallback(async (participant: OneOnOneParticipant, session: OneOnOneSessionSlot) => {
    // MED-1346: staff open every undated 1:1 in the drawer, so "Flag late change"
    // stays in reach for a late cancel the system never caught. The drawer shows it
    // when the 1:1 had an earlier date. Check-ins keep the coach list rule.
    if (
      session.status === 'unscheduled' &&
      isCheckinOneOnOneType(session.one_on_one_type) &&
      !undatedOneOnOneOpensDrawer(session)
    ) {
      // Unscheduled: fetch full session and open edit modal to schedule it
      try {
        const fullSession = await sessionsApi.getById(cohort.id, session.id);
        setEditOneOnOneSession({ session: fullSession, participant });
      } catch {
        // Fallback: open detail drawer
        setSelectedOneOnOneParticipant(participant);
        setSelectedOneOnOneSessionId(session.id);
        setOneOnOneDetailOpened(true);
      }
    } else {
      // Scheduled/completed: open detail drawer
      setSelectedOneOnOneParticipant(participant);
      setSelectedOneOnOneSessionId(session.id);
      setOneOnOneDetailOpened(true);
    }
  }, [cohort.id]);

  // ---- Event Handlers ----

  const handleEditSession = (session: Session) => {
    modals.setSelectedSession(session);
    modals.openEditModal();
  };

  const handleOpenSessionDetailModal = (session: Session) => {
    setDrawerDefaultTab(undefined);
    modals.setSelectedSession(session);
    modals.openSessionDetailModal();
  };

  // MED-702: open the session detail drawer given just a session id (by-session view clicks)
  const handleOpenSessionById = useCallback(
    (sessionId: string) => {
      const match = data.allSessions.find((s) => s.id === sessionId);
      if (match) {
        handleOpenSessionDetailModal(match as unknown as Session);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.allSessions],
  );
  const handleHuddleClickById = useCallback((_huddleId: string) => {
    // Placeholder: huddle detail navigation lives in coach/CM huddle modals elsewhere
  }, []);

  const handleAttendanceComplete = () => {
    data.loadData();
    onAttendanceComplete?.();
  };

  const handleSessionUpdate = (updated: Session) => {
    onSessionUpdate?.(updated);
    data.loadData();
  };

  const handleOpenFinalizeAll = () => {
    modals.setSelectedGroupIds(undefined);
    modals.setFinalizeMode('finalize');
    modals.openFinalizeModal();
  };

  const handleOpenFinalizeGroup = (groupId: string, isResend: boolean) => {
    modals.setSelectedGroupIds([groupId]);
    modals.setFinalizeMode(isResend ? 'resend' : 'finalize');
    modals.openFinalizeModal();
  };

  const handleFinalizationComplete = () => {
    data.loadData();
  };

  const handleOpenTopicModalForSession = (session: Parameters<typeof modals.setSelectedSessionForTopic>[0]) => {
    modals.setSelectedSessionForTopic(session);
    modals.openTopicModal();
  };

  const handleTopicSelectForSession = async (
    topic: Parameters<typeof data.handleTopicSelectForSession>[0],
    displayName?: string | null,
  ) => {
    if (!modals.selectedSessionForTopic) return;
    await data.handleTopicSelectForSession(topic, modals.selectedSessionForTopic, displayName);
  };

  // Cohort event action handlers
  const handleCohortEventSendInvites = (event: CohortEventWithDetails) => {
    modals.setSelectedCohortEvent(event);
    modals.setSendEventInvitesMode('send');
    modals.openSendEventInvites();
  };

  const handleCohortEventResendInvites = (event: CohortEventWithDetails) => {
    modals.setSelectedCohortEvent(event);
    modals.setSendEventInvitesMode('resend');
    modals.openSendEventInvites();
  };

  const handleCohortEventEditDateTime = (event: CohortEventWithDetails) => {
    modals.setSelectedCohortEvent(event);
    modals.openEditEventDateTime();
  };

  const handleCohortEventViewRsvps = (event: CohortEventWithDetails) => {
    modals.setSelectedCohortEvent(event);
    modals.openViewEventRsvps();
  };

  const handleCohortEventMarkAttendance = (event: CohortEventWithDetails) => {
    modals.setSelectedCohortEvent(event);
    modals.openEventAttendance();
  };

  const handleParticipantClick = async (cohortParticipantId: string) => {
    try {
      const participant = await participantsApi.getById(cohort.id, cohortParticipantId);
      modals.setSelectedParticipant(participant);
      modals.openParticipantDetail();
    } catch {
      // Silent fail — participant may have been removed
    }
  };

  const handleOpenSurveyResults = (
    groupId: string,
    groupName: string,
    coachName: string | undefined,
    tab?: 'post_session' | 'midpoint' | 'endpoint'
  ) => {
    modals.setSurveyResultsGroupId(groupId);
    modals.setSurveyResultsGroupName(groupName);
    modals.setSurveyResultsCoachName(coachName);
    modals.setSurveyResultsInitialTab(tab);
    modals.openSurveyResultsModal();
  };

  // MED-777: Add Time Slot modal state for Sessions tab zero state
  const [addTimeSlotGroupId, setAddTimeSlotGroupId] = useState<string | null>(null);
  const addTimeSlotGroup = useMemo(
    () => (addTimeSlotGroupId ? data.groups.find((g) => g.id === addTimeSlotGroupId) ?? null : null),
    [addTimeSlotGroupId, data.groups],
  );

  // ---- Computed values ----

  // Exclude cohort-wide sessions (group_id = null) from view logic — those are
  // template sessions for custom date lookup, not actual scheduled sessions
  const groupSessions = useMemo(
    () => data.allSessions.filter((s) => s.group_id),
    [data.allSessions]
  );
  const showGroupView =
    data.modalities.hasGroupCoaching && data.groups.length > 0 && groupSessions.length > 0;
  const showZeroState =
    (data.modalities.hasGroupCoaching && data.groups.length === 0) ||
    (data.modalities.hasGroupCoaching && data.groups.length > 0 && groupSessions.length === 0) ||
    (data.modalities.hasWorkshop && !data.modalities.hasGroupCoaching);
  // MED-1353: the same fallback as the coach Sessions tab (MED-1354). When no
  // group card is on screen, the 1:1 section holds the "Booking links" buttons.
  const groupCardsShown =
    showGroupView && groupingMode === 'date' && data.filteredGroups.length > 0;
  const oneOnOneBookingLinks =
    !groupCardsShown && !isPeerLedGroupCoaching(cohort) && data.groups.length > 0
      ? data.groups.map((group) => (
          <GroupBookingLinks
            key={group.id}
            groupId={group.id}
            groupName={group.name}
            label={data.groups.length > 1 ? `Booking links: ${group.name}` : undefined}
            context="oneOnOne"
            showGroupName={data.groups.length > 1}
            viewer="staff"
            hasCoach={Boolean(group.coach_id)}
          />
        ))
      : null;
  const groupsForFilter = data.groups.map((g) => ({ id: g.id, name: g.name }));
  const coachesForFilter = useMemo(() => {
    const uniqueCoaches = new Map<string, string>();
    data.groups.forEach((g) => {
      if (g.coach_name) {
        uniqueCoaches.set(g.coach_name, g.coach_name);
      }
    });
    return Array.from(uniqueCoaches.values()).sort();
  }, [data.groups]);

  // Determine if finalize banner should show
  // MED-665: Calendar invites are specific to group coaching, so only show the banner on
  // the "All" and "Group Coaching" tabs. Hide on "1:1 Coaching" and "Workshops" tabs.
  const showFinalizeBanner = useMemo(() => {
    if (!data.finalizationStatus?.ready || data.finalizationStatus.unfinalized_groups.length === 0) {
      return false;
    }
    if (activeTab !== 'all' && activeTab !== 'groupCoaching') return false;
    return data.sessionCounts.groupCoaching > 0 && data.groups.length > 0;
  }, [data.finalizationStatus, activeTab, data.sessionCounts, data.groups.length]);

  // MED-703: A modality is only "ready to announce" once it is actually configured.
  // Before groups/sessions/participants exist, the modality row exists but nothing is
  // scheduled — the banner must stay hidden until the configuration is real.
  const isModalityConfigured = useCallback(
    (modalityType: ModalityType, modalityId: string): boolean => {
      if (modalityType === 'group_coaching') {
        return data.groups.length > 0 && data.sessionCounts.groupCoaching > 0;
      }
      if (modalityType === 'one_on_one') {
        // MED-1350: check-in rows are not paid 1:1s. The API counts, cohort
        // wide, the participants with a paid 1:1 row.
        return data.oneOnOnePaidParticipantTotal > 0;
      }
      if (modalityType === 'workshop') {
        return workshops.some((w) => w.modality_id === modalityId && !!w.scheduled_start);
      }
      return false;
    },
    [data.groups.length, data.sessionCounts.groupCoaching, data.oneOnOnePaidParticipantTotal, workshops],
  );

  // MED-563 E18-5: Determine if announce banner should show for the current tab's modality
  const announceTargetModality = useMemo(() => {
    if (activeTab === 'all') return null;
    const modalityTypeMap: Record<string, ModalityType> = {
      groupCoaching: 'group_coaching',
      oneOnOne: 'one_on_one',
      workshop: 'workshop',
    };
    const modalityType = modalityTypeMap[activeTab];
    if (!modalityType) return null;
    return (
      cohort.modalities?.find(
        (m) =>
          m.modality_type === modalityType &&
          !m.announced_at &&
          !locallyAnnouncedIds.has(m.id) &&
          isModalityConfigured(m.modality_type, m.id),
      ) ?? null
    );
  }, [activeTab, cohort.modalities, locallyAnnouncedIds, isModalityConfigured]);

  // MED-582: Determine if "Announced" status indicator should show (announced + invites sent)
  const announcedModality = useMemo(() => {
    if (activeTab === 'all') return null;
    const modalityTypeMap: Record<string, ModalityType> = {
      groupCoaching: 'group_coaching',
      oneOnOne: 'one_on_one',
      workshop: 'workshop',
    };
    const modalityType = modalityTypeMap[activeTab];
    if (!modalityType) return null;
    return (
      cohort.modalities?.find(
        (m) => m.modality_type === modalityType && !!m.announced_at && m.invites_sent,
      ) ?? null
    );
  }, [activeTab, cohort.modalities]);

  // MED-701: Info callout when no group coaching sessions exist because groups or
  // time slots are missing. Appears on All and Group Coaching tabs (where the schedule
  // grid renders) and is suppressed once the cohort is Live/Completed or the schedule
  // is fully populated (every group has a time slot AND sessions have been generated).
  const showNoTimeSlotsCallout = useMemo(() => {
    if (!data.modalities.hasGroupCoaching) return false;
    if (activeTab !== 'all' && activeTab !== 'groupCoaching') return false;
    if (cohort.status === 'live' || cohort.status === 'completed') return false;
    const noGroups = data.groups.length === 0;
    const anyGroupMissingTimeSlot = data.groups.some((g) => !g.scheduled_time_slot_id);
    const noSessions = groupSessions.length === 0;
    return noGroups || anyGroupMissingTimeSlot || noSessions;
  }, [
    data.modalities.hasGroupCoaching,
    activeTab,
    cohort.status,
    data.groups,
    groupSessions.length,
  ]);

  // MED-604: Determine if cohort-level announce banner should show on "All" tab
  // MED-703: Exclude modalities that aren't actually configured yet
  const unannouncedModalitiesOnAllTab = useMemo(() => {
    if (activeTab !== 'all') return [];
    return (
      cohort.modalities?.filter(
        (m) =>
          !m.announced_at &&
          !locallyAnnouncedIds.has(m.id) &&
          isModalityConfigured(m.modality_type, m.id),
      ) ?? []
    );
  }, [activeTab, cohort.modalities, locallyAnnouncedIds, isModalityConfigured]);

  // MED-1190: the Select Topic modal must show the SAME topic the session row
  // displays. The row renders the canonically-resolved `topic_name`, whose base
  // topic is the cohort-wide session-number topic (`session_number_config.topic`).
  // Resolve the modal's current topic id from `data.sessionTopics`
  // (GET /cohorts/:id/session-topics, keyed by session_number) — the same source
  // `currentDisplayName` uses below — so the CURRENT badge matches the row. Both
  // the pencil modal and the Content Editor now write that one session-number
  // topic, so there is no per-group override to reconcile. Fall back to the raw
  // per-group `topic_id` only when a session has no session-number entry yet.
  const currentTopicIdForModal = useMemo(() => {
    const sessionNumber = modals.selectedSessionForTopic?.session_number;
    if (sessionNumber != null) {
      const resolved = data.sessionTopics.find((t) => t.session_number === sessionNumber);
      if (resolved) return resolved.topic_id ?? null;
    }
    return modals.selectedSessionForTopic?.topic_id ?? null;
  }, [modals.selectedSessionForTopic, data.sessionTopics]);

  // ---- Render ----

  return (
    <Stack gap={0}>
      {/* Sub-Navigation */}
      <SessionsSubNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        counts={data.sessionCounts}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        groupFilter={groupFilter}
        onGroupFilterChange={setGroupFilter}
        coachFilter={coachFilter}
        onCoachFilterChange={setCoachFilter}
        groups={groupsForFilter}
        coaches={coachesForFilter}
        showGroupCoaching={data.modalities.hasGroupCoaching}
        // MED-1350: check-ins are 1:1 rows, so the 1:1 tab shows for them
        // whatever the multi_modality flag says (Review Focus 1).
        showOneOnOne={showsOneOnOneSection(data.modalities, multiModalityEnabled)}
        showWorkshop={data.modalities.hasWorkshop && multiModalityEnabled}
        groupingMode={groupingMode}
        onGroupingModeChange={setGroupingMode}
        showGroupingToggle={
          activeTab === 'all' || activeTab === 'groupCoaching' || activeTab === 'oneOnOne'
        }
      />

      {/* MED-701: No time slots / no sessions info callout */}
      {showNoTimeSlotsCallout && (
        <Alert
          icon={<Info size={18} />}
          color="blue"
          variant="light"
          mb="md"
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="sm">
              No sessions yet — groups need to be created and have a selected time slot
              before sessions can be created. Set time slots for each group in the Groups tab.
            </Text>
            {onNavigateToGroups && (
              <Button
                variant="subtle"
                color="blue"
                size="sm"
                onClick={onNavigateToGroups}
              >
                Go to Groups
              </Button>
            )}
          </Group>
        </Alert>
      )}

      {/* MED-604: Cohort-level announce banner on All tab */}
      {unannouncedModalitiesOnAllTab.length > 0 && (
        <Alert
          icon={<Bell size={18} color="#2D4739" />}
          title="Ready to announce to participants"
          mb="md"
          styles={{
            root: { backgroundColor: '#E8F5E9', border: '1px solid #C8E6C9' },
            body: { gap: 0 },
            title: { color: '#2D4739', marginBottom: 0 },
            icon: { color: '#2D4739' },
            message: { color: '#2D4739', marginTop: 0 },
          }}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="sm" c="#2D4739">
              {unannouncedModalitiesOnAllTab.length === 1
                ? '1 modality is configured but hasn\'t been announced yet.'
                : `${unannouncedModalitiesOnAllTab.length} modalities are configured but haven't been announced yet.`}{' '}
              Participants won&apos;t see them until you finalize and announce.
            </Text>
            {!participantFeaturesOff && (
              <Button
                leftSection={<Bell size={14} />}
                onClick={() => setCohortAnnounceModalOpened(true)}
                size="sm"
              >
                Finalize &amp; Announce to Participants
              </Button>
            )}
          </Group>
        </Alert>
      )}

      {/* MED-563 E18-5: Announce to Participants Callout (shows before calendar invite step) */}
      {announceTargetModality && (
        <Alert
          icon={<Bell size={18} color="#2D4739" />}
          title="Ready to announce to participants"
          mb="md"
          styles={{
            root: { backgroundColor: '#E8F5E9', border: '1px solid #C8E6C9' },
            body: { gap: 0 },
            title: { color: '#2D4739', marginBottom: 0 },
            icon: { color: '#2D4739' },
            message: { color: '#2D4739', marginTop: 0 },
          }}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="sm" c="#2D4739">
              This modality is configured but hasn&apos;t been announced yet. Participants won&apos;t
              see it until you finalize and announce.
            </Text>
            {!participantFeaturesOff && (
              <Button
                leftSection={<Bell size={14} />}
                onClick={() =>
                  handleOpenAnnounceModal(
                    announceTargetModality.id,
                    announceTargetModality.modality_type,
                  )
                }
                size="sm"
              >
                Finalize &amp; Announce to Participants
              </Button>
            )}
          </Group>
        </Alert>
      )}

      {/* Readiness Callout Banner (E5-19: only in modality tabs, MED-582: only after modality announced, hidden once invites sent) */}
      {showFinalizeBanner && data.finalizationStatus && !announceTargetModality && !announcedModality && (
        <Alert
          icon={<CheckCircle size={18} color="#2D4739" />}
          title="All session dates are set — ready to send calendar invites"
          mb="md"
          styles={{
            root: { backgroundColor: '#E8F5E9', border: '1px solid #C8E6C9' },
            body: { gap: 0 },
            title: { color: '#2D4739', marginBottom: 0 },
            icon: { color: '#2D4739' },
            message: { color: '#2D4739', marginTop: 0 },
          }}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="sm" c="#2D4739">
              {data.finalizationStatus.total_sessions} sessions across{' '}
              {data.finalizationStatus.unfinalized_groups.length} group
              {data.finalizationStatus.unfinalized_groups.length !== 1 ? 's' : ''}.
              {data.finalizationStatus.date_range.start && data.finalizationStatus.date_range.end && (
                <>
                  {' '}
                  {formatDate(data.finalizationStatus.date_range.start)} -{' '}
                  {formatDate(data.finalizationStatus.date_range.end)}.
                </>
              )}{' '}
              Review schedules and send calendar invitations.
            </Text>
            <Button
              leftSection={<Send size={14} />}
              onClick={handleOpenFinalizeAll}
              size="sm"
            >
              Send Calendar Invites
            </Button>
          </Group>
        </Alert>
      )}

      {/* MED-582: Announced status indicator (announced + invites sent) */}
      {announcedModality && (
        <Alert
          icon={<CheckCircle size={18} color="#2D4739" />}
          title="Announced"
          mb="md"
          styles={{
            root: { backgroundColor: '#E8F5E9', border: '1px solid #C8E6C9' },
            body: { gap: 0 },
            title: { color: '#2D4739', marginBottom: 0 },
            icon: { color: '#2D4739' },
            message: { color: '#2D4739', marginTop: 0 },
          }}
        >
          <Text size="sm" c="#2D4739">
            Participants have been announced and calendar invites have been sent (announced{' '}
            {formatDate(announcedModality.announced_at!)}).
          </Text>
        </Alert>
      )}

      {/* MED-100: Survey Results Callout Banner */}
      <SurveyResultsCallout
        cohortId={cohort.id}
        onReviewClick={() => {
          modals.setSurveyResultsGroupId(undefined);
          modals.setSurveyResultsGroupName(undefined);
          modals.setSurveyResultsCoachName(undefined);
          modals.setSurveyResultsInitialTab(undefined);
          modals.openSurveyResultsModal();
        }}
      />

      {/* Main Content */}
      {/* MED-1350: the loader shows on the first load only, so a save does
          not unmount the content and jump the page to the top. */}
      {data.loading && !data.hasLoaded ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : viewMode === 'calendar' ? (
        <SessionsCalendarView
          cohortId={cohort.id}
          sessions={data.allSessions}
          groups={data.groups}
          activeTab={activeTab}
          searchQuery={searchQuery}
          groupFilter={groupFilter}
          coachFilter={coachFilter}
          cohortEventsKey={data.cohortEventsKey}
          onSessionClick={handleOpenSessionDetailModal}
        />
      ) : showZeroState ? (
        <ZeroStateContent
          activeTab={activeTab}
          cohort={cohort}
          modalities={data.modalities}
          sessionTopics={data.sessionTopics}
          sessions={data.allSessions}
          cohortEventsKey={data.cohortEventsKey}
          oneOnOneParticipants={data.oneOnOneParticipants}
          oneOnOneTotal={data.oneOnOneTotal}
          onLoadMoreOneOnOne={data.loadMoreOneOnOne}
          loadingMoreOneOnOne={data.loadingMoreOneOnOne}
          oneOnOneViewMode={oneOnOneViewMode}
          onOneOnOneViewModeChange={setOneOnOneViewMode}
          onNavigateToOneOnOne={() => setActiveTab('oneOnOne')}
          onTopicChange={data.handleZeroStateTopicChange}
          onOpenHuddleModal={modals.openHuddleModal}
          onCohortEventEditDateTime={handleCohortEventEditDateTime}
          onCohortEventSendInvites={handleCohortEventSendInvites}
          onCohortEventResendInvites={handleCohortEventResendInvites}
          onCohortEventViewRsvps={handleCohortEventViewRsvps}
          onCohortEventMarkAttendance={handleCohortEventMarkAttendance}
          onAssignOneOnOneCoach={handleAssignOneOnOneCoach}
          onOneOnOneSessionClick={handleOneOnOneSessionClick}
          workshops={workshops}
          workshopPattern={workshopPattern}
          onWorkshopUpdated={handleWorkshopUpdated}
          oneOnOneBookingLinks={oneOnOneBookingLinks}
        />
      ) : showGroupView ? (
        <GroupViewContent
          activeTab={activeTab}
          cohort={cohort}
          modalities={data.modalities}
          filteredGroups={data.filteredGroups}
          oneOnOneParticipants={data.oneOnOneParticipants}
          oneOnOneTotal={data.oneOnOneTotal}
          onLoadMoreOneOnOne={data.loadMoreOneOnOne}
          loadingMoreOneOnOne={data.loadingMoreOneOnOne}
          oneOnOneViewMode={oneOnOneViewMode}
          onOneOnOneViewModeChange={setOneOnOneViewMode}
          onNavigateToOneOnOne={() => setActiveTab('oneOnOne')}
          expandedGroups={data.expandedGroups}
          showOneOnOnes={data.showOneOnOnes}
          cohortHuddles={data.cohortHuddles}
          cohortEventsKey={data.cohortEventsKey}
          checkInCount={data.cohortHuddles.length}
          onToggleGroup={data.toggleGroup}
          onSessionClick={handleOpenSessionDetailModal}
          onEditSession={handleEditSession}
          onOpenTopicModal={handleOpenTopicModalForSession}
          onFinalizeGroup={handleOpenFinalizeGroup}
          onOpenSurveyResults={handleOpenSurveyResults}
          onOpenCoachFeedback={handleOpenCoachFeedback}
          onSetShowOneOnOnes={data.setShowOneOnOnes}
          onExpandAllGroups={data.expandAllGroups}
          onCollapseAllGroups={data.collapseAllGroups}
          allGroupsExpanded={data.allGroupsExpanded}
          onOpenHuddleModal={modals.openHuddleModal}
          finalizationStatus={data.finalizationStatus}
          onFinalizeAll={handleOpenFinalizeAll}
          onCohortEventEditDateTime={handleCohortEventEditDateTime}
          onCohortEventSendInvites={handleCohortEventSendInvites}
          onCohortEventResendInvites={handleCohortEventResendInvites}
          onCohortEventViewRsvps={handleCohortEventViewRsvps}
          onCohortEventMarkAttendance={handleCohortEventMarkAttendance}
          onParticipantClick={handleParticipantClick}
          onOpenFollowUps={onOpenFollowUps}
          onAssignOneOnOneCoach={handleAssignOneOnOneCoach}
          onOneOnOneSessionClick={handleOneOnOneSessionClick}
          workshops={workshops}
          workshopPattern={workshopPattern}
          onWorkshopUpdated={handleWorkshopUpdated}
          oneOnOneBookingLinks={oneOnOneBookingLinks}
          groupingMode={groupingMode}
          onOpenBulkTopicEditor={() => setBulkTopicEditorOpened(true)}
          bySessionRefreshKey={bySessionRefreshKey}
          onOpenSessionById={handleOpenSessionById}
          onHuddleClick={handleHuddleClickById}
          totalParticipantsForCheckins={data.oneOnOneParticipants.length || data.groups.length || 0}
          onAddTimeSlot={setAddTimeSlotGroupId}
        />
      ) : groupSessions.length > 0 ? (
        <FlatSessionList
          sessions={groupSessions}
          onSessionClick={handleEditSession}
          onOpenTopicModal={handleOpenTopicModalForSession}
        />
      ) : data.modalities.hasGroupCoaching && data.groups.length > 0 ? (
        <NoSessionsZeroState groupCount={data.groups.length} />
      ) : (
        <NoGroupsZeroState onNavigateToParticipants={onNavigateToParticipants} />
      )}

      {/* MED-1389: link bookings with no session. Cancel and remove refuse
          impersonation, so not shown then. The 1:1 tab always shows it; the
          All tab only when there is one. */}
      {!isImpersonating &&
        viewMode !== 'calendar' &&
        (activeTab === 'oneOnOne' || activeTab === 'all') &&
        showsOneOnOneSection(data.modalities, multiModalityEnabled) && (
          <Box mt="md">
            <UnattachedBookingsSection
              cohortId={cohort.id}
              hideWhenEmpty={activeTab === 'all'}
              onLinked={() => {
                data.loadData();
                bumpBySessionRefresh();
              }}
            />
          </Box>
        )}

      {/* ---- Modals ---- */}
      <BulkTopicEditorModal
        cohortId={cohort.id}
        opened={bulkTopicEditorOpened}
        onClose={() => setBulkTopicEditorOpened(false)}
        onSaved={bumpBySessionRefresh}
      />
      <ExportModal
        opened={modals.exportModalOpened}
        onClose={modals.closeExportModal}
        entityType="sessions"
        contextType="cohort"
        contextId={cohort.id}
      />

      <EditSessionModal
        opened={modals.editModalOpened}
        onClose={modals.closeEditModal}
        session={modals.selectedSession}
        cohort={cohort}
        onUpdate={handleSessionUpdate}
      />

      <FinalizeInvitesModal
        opened={modals.finalizeModalOpened}
        onClose={modals.closeFinalizeModal}
        cohort={cohort}
        groupIds={modals.selectedGroupIds}
        onFinalize={handleFinalizationComplete}
        mode={modals.finalizeMode}
      />

      {/* MED-604: Cohort-level announce modal */}
      <AnnounceCohortModal
        opened={cohortAnnounceModalOpened}
        onClose={() => setCohortAnnounceModalOpened(false)}
        cohortId={cohort.id}
        onSuccess={handleCohortAnnounceSuccess}
        emailDisabled={isAnnouncementEmailDisabled}
      />

      {/* MED-563 E18-5: Announce modality modal */}
      {announceModalityId && announceModalityType && (
        <AnnounceModalityModal
          opened={!!announceModalityId}
          onClose={() => {
            setAnnounceModalityId(null);
            setAnnounceModalityType(null);
          }}
          cohortId={cohort.id}
          modalityId={announceModalityId}
          modalityType={announceModalityType}
          onSuccess={handleAnnounceSuccess}
          emailDisabled={isAnnouncementEmailDisabled}
        />
      )}

      <TopicSelectModal
        opened={modals.topicModalOpened}
        onClose={modals.closeTopicModal}
        onSelect={handleTopicSelectForSession}
        currentTopicId={currentTopicIdForModal}
        // MED-851 AC3: pre-populate the participant-facing name from the
        // canonical cohort_session_topics row for this session number, since
        // display_name lives at the (cohort, session_number) scope rather
        // than per-group.
        currentDisplayName={
          modals.selectedSessionForTopic?.session_number != null
            ? (data.sessionTopics.find(
                (t) => t.session_number === modals.selectedSessionForTopic?.session_number,
              )?.display_name ?? null)
            : null
        }
        sessionNumber={modals.selectedSessionForTopic?.session_number || undefined}
        groupName={
          modals.selectedSessionForTopic
            ? data.groups.find((g) => g.id === modals.selectedSessionForTopic?.group_id)?.name
            : undefined
        }
      />

      <AddHuddleModal
        opened={modals.huddleModalOpened}
        onClose={modals.closeHuddleModal}
        onAdd={data.handleAddHuddle}
      />

      {modals.selectedSession && (
        <SessionDetailDrawer
          opened={modals.sessionDetailModalOpened}
          onClose={modals.closeSessionDetailModal}
          cohortId={cohort.id}
          sessionId={modals.selectedSession.id}
          sessionNumber={modals.selectedSession.session_number}
          topicName={modals.selectedSession.topic_name || null}
          groupName={
            data.groups.find((g) => g.id === modals.selectedSession?.group_id)?.name || '—'
          }
          groupId={modals.selectedSession.group_id || undefined}
          scheduledStart={modals.selectedSession.scheduled_start}
          timezone={modals.selectedSession.timezone || null}
          displayStatus={getDisplayStatus(modals.selectedSession)}
          coachName={
            data.groups.find((g) => g.id === modals.selectedSession?.group_id)?.coach_name || null
          }
          attendanceMarked={modals.selectedSession.attendance_marked}
          feedbackSubmitted={
            (modals.selectedSession as unknown as { feedback_submitted?: boolean })
              .feedback_submitted
          }
          // MED-1400: the full list, as the Resources tab gets it, so "All sessions" is the same set.
          sessions={data.allSessions}
          categories={resourceCategories}
          onComplete={handleAttendanceComplete}
          defaultTab={drawerDefaultTab}
          onOpenFollowUps={onOpenFollowUps ? handleOpenFollowUps : undefined}
          liveSessionDeliveryModalityEnabled={groupCoachingLiveSessionDeliveryEnabled}
        />
      )}

      <SendCohortEventInvitesModal
        opened={modals.sendEventInvitesOpened}
        onClose={modals.closeSendEventInvites}
        cohortId={cohort.id}
        event={modals.selectedCohortEvent}
        mode={modals.sendEventInvitesMode}
        onSent={data.handleCohortEventRefresh}
      />

      <EditCohortEventDateTimeModal
        opened={modals.editEventDateTimeOpened}
        onClose={modals.closeEditEventDateTime}
        cohortId={cohort.id}
        event={modals.selectedCohortEvent}
        onSaved={data.handleCohortEventRefresh}
      />

      <ViewCohortEventRsvpsModal
        opened={modals.viewEventRsvpsOpened}
        onClose={modals.closeViewEventRsvps}
        cohortId={cohort.id}
        eventId={modals.selectedCohortEvent?.id || null}
        eventName={modals.selectedCohortEvent?.event_name || ''}
      />

      <CohortEventDetailDrawer
        opened={modals.eventAttendanceOpened}
        onClose={modals.closeEventAttendance}
        cohortId={cohort.id}
        event={modals.selectedCohortEvent}
        onComplete={data.handleCohortEventRefresh}
        categories={resourceCategories}
      />

      <SurveyResultsModal
        opened={modals.surveyResultsModalOpened}
        onClose={modals.closeSurveyResultsModal}
        cohortId={cohort.id}
        groupId={modals.surveyResultsGroupId}
        groupName={modals.surveyResultsGroupName}
        coachName={modals.surveyResultsCoachName}
        initialTab={modals.surveyResultsInitialTab}
      />

      <CoachFeedbackReviewModal
        opened={coachFeedbackModalOpen}
        onClose={() => setCoachFeedbackModalOpen(false)}
        cohortId={cohort.id}
        surveyType={coachFeedbackSurveyType}
      />

      <ParticipantDetailModal
        opened={modals.participantDetailOpened}
        onClose={() => {
          modals.closeParticipantDetail();
          modals.setSelectedParticipant(null);
        }}
        participant={modals.selectedParticipant}
        cohortId={cohort.id}
        groups={data.groups.map((g) => ({ id: g.id, name: g.name, coach_name: g.coach_name }))}
        onUpdate={data.loadData}
        onDelete={data.loadData}
      />

      {/* E18-3: 1:1 Session Detail Drawer */}
      {selectedOneOnOneParticipant && selectedOneOnOneSessionId && (
        <OneOnOneSessionDetailDrawer
          opened={oneOnOneDetailOpened}
          onClose={() => setOneOnOneDetailOpened(false)}
          cohortId={cohort.id}
          sessionId={selectedOneOnOneSessionId}
          participantName={`${selectedOneOnOneParticipant.first_name} ${selectedOneOnOneParticipant.last_name}`}
          participantAvatarUrl={selectedOneOnOneParticipant.avatar_url}
          coachName={selectedOneOnOneParticipant.coach_name}
          coachAvatarUrl={selectedOneOnOneParticipant.coach_avatar_url}
          categories={resourceCategories}
          totalSessions={selectedOneOnOneParticipant.total_session_count}
          // MED-1346: from the live list, so a new attendance mark shows after the reload.
          attendanceStatus={
            data.oneOnOneParticipants
              .flatMap((p) => p.sessions)
              .find((s) => s.id === selectedOneOnOneSessionId)?.attendance_status
          }
          participantUserId={selectedOneOnOneParticipant.user_id}
          onComplete={() => {
            data.loadData();
            onAttendanceComplete?.();
          }}
          // MED-1350: date edit (and Clear date) from a dated block.
          onEditDate={(fullSession) => {
            setOneOnOneDetailOpened(false);
            setEditOneOnOneSession({
              session: fullSession,
              participant: selectedOneOnOneParticipant,
            });
          }}
        />
      )}

      {/* E18-3: Edit 1:1 Session Modal */}
      {editOneOnOneSession && (
        <EditSessionModal
          opened={editOneOnOneSession !== null}
          onClose={() => setEditOneOnOneSession(null)}
          session={editOneOnOneSession.session}
          cohort={cohort}
          isOneOnOne
          participantName={`${editOneOnOneSession.participant.first_name} ${editOneOnOneSession.participant.last_name}`}
          onUpdate={(updated) => {
            setEditOneOnOneSession(null);
            data.loadData();
            onSessionUpdate?.(updated);
          }}
        />
      )}

      {/* E18-2: Assign 1:1 Coach Modal */}
      <AssignOneOnOneCoachModal
        cohortId={cohort.id}
        participant={assignCoachParticipant}
        opened={assignCoachParticipant !== null}
        onClose={() => setAssignCoachParticipant(null)}
        onAssigned={() => {
          setAssignCoachParticipant(null);
          data.loadData();
        }}
      />

      {/* MED-777: Add Time Slot modal for groups without time slots on Sessions tab */}
      <AddTimeSlotModal
        opened={addTimeSlotGroupId !== null}
        onClose={() => setAddTimeSlotGroupId(null)}
        cohortId={cohort.id}
        group={addTimeSlotGroup}
        timeSlots={data.timeSlots}
        onAssigned={() => {
          setAddTimeSlotGroupId(null);
          data.loadData();
        }}
      />

    </Stack>
  );
}

// ---- Internal composition components ----

import type { CohortSessionTopic } from '../../../types';
import type { GroupWithSessions } from './sessions/sessionsHelpers';

interface ZeroStateContentProps {
  activeTab: SessionTabValue;
  cohort: CohortWithDetails;
  modalities: { hasGroupCoaching: boolean; hasOneOnOne: boolean; hasWorkshop: boolean; hasCheckins: boolean };
  sessionTopics: CohortSessionTopic[];
  sessions: Session[];
  cohortEventsKey: number;
  oneOnOneParticipants: import('../../../types').OneOnOneParticipant[];
  oneOnOneTotal: number;
  onLoadMoreOneOnOne: () => Promise<void>;
  loadingMoreOneOnOne: boolean;
  oneOnOneViewMode: OneOnOneViewMode;
  onOneOnOneViewModeChange: (mode: OneOnOneViewMode) => void;
  onNavigateToOneOnOne?: () => void;
  onTopicChange: (sessionNumber: number, topicId: string, topicName: string) => void;
  onOpenHuddleModal: () => void;
  onCohortEventEditDateTime: (event: CohortEventWithDetails) => void;
  onCohortEventSendInvites: (event: CohortEventWithDetails) => void;
  onCohortEventResendInvites: (event: CohortEventWithDetails) => void;
  onCohortEventViewRsvps: (event: CohortEventWithDetails) => void;
  onCohortEventMarkAttendance: (event: CohortEventWithDetails) => void;
  onAssignOneOnOneCoach?: (participant: import('../../../types').OneOnOneParticipant) => void;
  onOneOnOneSessionClick?: (participant: import('../../../types').OneOnOneParticipant, session: import('../../../types').OneOnOneSessionSlot) => void;
  workshops: WorkshopWithSession[];
  workshopPattern: 'sequential' | 'parallel';
  onWorkshopUpdated: () => void;
  /** MED-1353: "Booking links" buttons for the 1:1 section header. */
  oneOnOneBookingLinks?: ReactNode;
}

function ZeroStateContent({
  activeTab,
  cohort,
  modalities,
  sessionTopics,
  sessions,
  cohortEventsKey,
  oneOnOneParticipants,
  oneOnOneTotal,
  onLoadMoreOneOnOne,
  loadingMoreOneOnOne,
  oneOnOneViewMode,
  onOneOnOneViewModeChange,
  onNavigateToOneOnOne,
  onTopicChange,
  onOpenHuddleModal,
  onCohortEventEditDateTime,
  onCohortEventSendInvites,
  onCohortEventResendInvites,
  onCohortEventViewRsvps,
  onCohortEventMarkAttendance,
  onAssignOneOnOneCoach,
  onOneOnOneSessionClick,
  workshops,
  workshopPattern,
  onWorkshopUpdated,
  oneOnOneBookingLinks,
}: ZeroStateContentProps) {
  const multiModalityEnabled = useFeatureFlag('multi_modality');
  const cohortEventsProps = {
    cohortId: cohort.id,
    refreshTrigger: cohortEventsKey,
    onAddHuddle: onOpenHuddleModal,
    onScheduleEvent: onCohortEventEditDateTime,
    onEditDateTime: onCohortEventEditDateTime,
    onSendInvites: onCohortEventSendInvites,
    onResendInvites: onCohortEventResendInvites,
    onViewRsvps: onCohortEventViewRsvps,
    onMarkAttendance: onCohortEventMarkAttendance,
    onEventClick: onCohortEventMarkAttendance,
    // MED-1219: hide coach-only affordances on peer-led cohorts. This zero-state
    // path is the one in the reported screenshot ("No cohort events found" with
    // the Add Huddle button still showing). Gate on the saved coaching sub-type
    // alone (AC6), matching GroupViewContent's isPeerLedCohort derivation.
    isPeerLed: isPeerLedGroupCoaching(cohort),
  };

  const oneOnOneSessionCount = cohort.modalities?.find((m) => m.modality_type === 'one_on_one')?.session_count ?? 2;
  // MED-1350: check-ins show in the 1:1 section even with no 1:1 modality
  // and whatever the multi_modality flag says (Review Focus 1).
  const showOneOnOneSection = showsOneOnOneSection(modalities, multiModalityEnabled);
  const checkinsOnly = !modalities.hasOneOnOne;
  // MED-1038: workshop modality config for the Workshops header AI pill
  const workshopAiFeaturesEnabled =
    cohort.modalities?.find((m) => m.modality_type === 'workshop')?.ai_features_enabled ?? false;

  return (
    <Stack gap={0}>
      {activeTab === 'all' && (
        <>
          <CohortEventsSection key={cohortEventsKey} {...cohortEventsProps} />
          {modalities.hasGroupCoaching && (
            <GroupCoachingSessionsSection
              cohort={cohort}
              sessionTopics={sessionTopics}
              sessions={sessions}
              onTopicChange={onTopicChange}
            />
          )}
          {showOneOnOneSection && (
            <Box mt="md">
              <OneOnOneCoachingSection
                participants={oneOnOneParticipants}
                sessionCount={oneOnOneSessionCount}
                checkinsOnly={checkinsOnly}
                checkinCount={enabledCheckinCount(cohort)}
                total={oneOnOneTotal}
                useAccordion
                isExpanded
                onShowMore={onNavigateToOneOnOne}
                onAssignCoach={onAssignOneOnOneCoach}
                onSessionClick={onOneOnOneSessionClick}
                historyCohortId={cohort.id}
                viewMode={oneOnOneViewMode}
                onViewModeChange={onOneOnOneViewModeChange}
                headerActions={oneOnOneBookingLinks}
              />
            </Box>
          )}
          {modalities.hasWorkshop && multiModalityEnabled && (
            <Box mt="md">
              <WorkshopSessionsSection
                cohortId={cohort.id}
                workshops={workshops}
                pattern={workshopPattern}
                useAccordion
                isExpanded
                onWorkshopUpdated={onWorkshopUpdated}
                aiFeaturesEnabled={workshopAiFeaturesEnabled}
              />
            </Box>
          )}
        </>
      )}
      {activeTab === 'groupCoaching' && (
        <GroupCoachingSessionsSection
          cohort={cohort}
          sessionTopics={sessionTopics}
          sessions={sessions}
          onTopicChange={onTopicChange}
        />
      )}
      {activeTab === 'oneOnOne' && showOneOnOneSection && (
        <OneOnOneCoachingSection
          participants={oneOnOneParticipants}
          sessionCount={oneOnOneSessionCount}
          checkinsOnly={checkinsOnly}
          checkinCount={enabledCheckinCount(cohort)}
          total={oneOnOneTotal}
          onLoadMore={onLoadMoreOneOnOne}
          loadingMore={loadingMoreOneOnOne}
          onAssignCoach={onAssignOneOnOneCoach}
          onSessionClick={onOneOnOneSessionClick}
          historyCohortId={cohort.id}
          viewMode={oneOnOneViewMode}
          onViewModeChange={onOneOnOneViewModeChange}
          headerActions={oneOnOneBookingLinks}
        />
      )}
      {activeTab === 'workshop' && multiModalityEnabled && (
        <WorkshopSessionsSection
          cohortId={cohort.id}
          workshops={workshops}
          pattern={workshopPattern}
          onWorkshopUpdated={onWorkshopUpdated}
          aiFeaturesEnabled={workshopAiFeaturesEnabled}
        />
      )}
    </Stack>
  );
}

interface GroupViewContentProps {
  activeTab: SessionTabValue;
  cohort: CohortWithDetails;
  modalities: { hasGroupCoaching: boolean; hasOneOnOne: boolean; hasWorkshop: boolean; hasCheckins: boolean };
  filteredGroups: GroupWithSessions[];
  oneOnOneParticipants: import('../../../types').OneOnOneParticipant[];
  oneOnOneTotal: number;
  onLoadMoreOneOnOne: () => Promise<void>;
  loadingMoreOneOnOne: boolean;
  oneOnOneViewMode: OneOnOneViewMode;
  onOneOnOneViewModeChange: (mode: OneOnOneViewMode) => void;
  onNavigateToOneOnOne?: () => void;
  expandedGroups: Set<string>;
  showOneOnOnes: boolean;
  cohortHuddles: import('../../../types').CoachHuddle[];
  cohortEventsKey: number;
  checkInCount: number;
  onToggleGroup: (groupId: string) => void;
  onSessionClick: (session: Session) => void;
  onEditSession: (session: Session) => void;
  onOpenTopicModal: (session: import('../../../services/api/sessions').SessionWithDetailsAndRsvp) => void;
  onFinalizeGroup: (groupId: string, isResend: boolean) => void;
  onOpenSurveyResults: (
    groupId: string,
    groupName: string,
    coachName: string | undefined,
    tab?: 'post_session' | 'midpoint' | 'endpoint'
  ) => void;
  onSetShowOneOnOnes: (show: boolean) => void;
  onExpandAllGroups: () => void;
  onCollapseAllGroups: () => void;
  allGroupsExpanded: boolean;
  onOpenHuddleModal: () => void;
  finalizationStatus?: { ready: boolean; total_sessions: number; unfinalized_groups: { id: string; name: string }[]; date_range: { start: string | null; end: string | null } } | null;
  onFinalizeAll?: () => void;
  onCohortEventEditDateTime: (event: CohortEventWithDetails) => void;
  onCohortEventSendInvites: (event: CohortEventWithDetails) => void;
  onCohortEventResendInvites: (event: CohortEventWithDetails) => void;
  onCohortEventViewRsvps: (event: CohortEventWithDetails) => void;
  onCohortEventMarkAttendance: (event: CohortEventWithDetails) => void;
  onOpenCoachFeedback?: (surveyType: 'midpoint' | 'endpoint') => void;
  onParticipantClick?: (participantId: string) => void;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
  onAssignOneOnOneCoach?: (participant: import('../../../types').OneOnOneParticipant) => void;
  onOneOnOneSessionClick?: (participant: import('../../../types').OneOnOneParticipant, session: import('../../../types').OneOnOneSessionSlot) => void;
  workshops: WorkshopWithSession[];
  workshopPattern: 'sequential' | 'parallel';
  onWorkshopUpdated: () => void;
  // MED-702: by-session grouping
  groupingMode: 'date' | 'session';
  onOpenBulkTopicEditor: () => void;
  bySessionRefreshKey: number;
  onOpenSessionById: (sessionId: string) => void;
  onHuddleClick: (huddleId: string) => void;
  totalParticipantsForCheckins: number;
  /** MED-777: Opens the Add Time Slot modal for a specific group */
  onAddTimeSlot?: (groupId: string) => void;
  /** MED-1353: "Booking links" buttons for the 1:1 section header. */
  oneOnOneBookingLinks?: ReactNode;
}

function GroupViewContent({
  activeTab,
  cohort,
  modalities,
  filteredGroups,
  oneOnOneParticipants,
  oneOnOneTotal,
  onLoadMoreOneOnOne,
  loadingMoreOneOnOne,
  oneOnOneViewMode,
  onOneOnOneViewModeChange,
  onNavigateToOneOnOne,
  expandedGroups,
  showOneOnOnes,
  cohortHuddles,
  cohortEventsKey,
  checkInCount,
  onToggleGroup,
  onSessionClick,
  onEditSession,
  onOpenTopicModal,
  onFinalizeGroup,
  onOpenSurveyResults,
  onSetShowOneOnOnes,
  onExpandAllGroups,
  onCollapseAllGroups,
  allGroupsExpanded,
  onOpenHuddleModal,
  finalizationStatus,
  onFinalizeAll,
  onCohortEventEditDateTime,
  onCohortEventSendInvites,
  onCohortEventResendInvites,
  onCohortEventViewRsvps,
  onCohortEventMarkAttendance,
  onOpenCoachFeedback,
  onParticipantClick,
  onOpenFollowUps,
  onAssignOneOnOneCoach,
  onOneOnOneSessionClick,
  workshops,
  workshopPattern,
  onWorkshopUpdated,
  groupingMode,
  onOpenBulkTopicEditor,
  bySessionRefreshKey,
  onOpenSessionById,
  onHuddleClick,
  totalParticipantsForCheckins,
  onAddTimeSlot,
  oneOnOneBookingLinks,
}: GroupViewContentProps) {
  const multiModalityEnabled = useFeatureFlag('multi_modality');
  // MED-1086 (E1.3m / AC2): per-modality live-session-delivery setting (second
  // switch of e1.3l's gate) for the per-row Build Content entry point. Same
  // derivation as SessionsTab; recomputed here since this nested component
  // doesn't close over the parent's local.
  const groupCoachingLiveSessionDeliveryEnabled =
    cohort.modalities?.find((m) => m.modality_type === 'group_coaching')
      ?.live_session_delivery_enabled ?? false;
  // MED-954 / E4.0d: Peer Led group_coaching cohorts have no coach, so coach
  // feedback surveys (AC6) do not apply.
  // MED-1202: this "has no coaches" determination follows the coaching sub-type
  // ALONE — it was previously gated on live_session_delivery (AC8/IV8), which
  // wrongly re-surfaced coach feedback for a peer-led + LSD-off cohort. Routed
  // through the shared selector so it stays consistent with the Groups view and
  // the backend. LSD gating remains only on the actual live/prep experience.
  const isPeerLedCohort = isPeerLedGroupCoaching(cohort);
  // Per-group session count from modality config (not total across all groups)
  const groupCoachingModality = cohort.modalities?.find(m => m.modality_type === 'group_coaching');
  const sessionsPerGroup = groupCoachingModality?.session_count || 6;
  // MED-1038: workshop modality config for the Workshops header AI pill
  const workshopAiFeaturesEnabled =
    cohort.modalities?.find(m => m.modality_type === 'workshop')?.ai_features_enabled ?? false;

  // Section collapse state — all expanded by default
  const [sectionExpanded, setSectionExpanded] = useState({
    cohortEvents: true,
    groupCoaching: true,
    oneOnOne: true,
    workshop: true,
  });

  const toggleSection = (section: keyof typeof sectionExpanded) => {
    setSectionExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Shared section container + header styles
  const sectionContainerStyle = {
    border: '1px solid #E5E2DC',
    borderRadius: 8,
    overflow: 'hidden' as const,
  };

  const getSectionHeaderStyle = (isOpen: boolean) => ({
    backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
    padding: '16px 20px',
    cursor: 'pointer' as const,
    borderBottom: isOpen ? '1px solid #E5E2DC' : 'none',
  });

  return (
    <Stack gap="md">
      {activeTab === 'all' && (
        <CohortEventsSection
          key={cohortEventsKey}
          cohortId={cohort.id}
          refreshTrigger={cohortEventsKey}
          onAddHuddle={onOpenHuddleModal}
          onScheduleEvent={onCohortEventEditDateTime}
          onEditDateTime={onCohortEventEditDateTime}
          onSendInvites={onCohortEventSendInvites}
          onResendInvites={onCohortEventResendInvites}
          onViewRsvps={onCohortEventViewRsvps}
          onMarkAttendance={onCohortEventMarkAttendance}
          onEventClick={onCohortEventMarkAttendance}
          isPeerLed={isPeerLedCohort}
        />
      )}

      {(activeTab === 'all' || activeTab === 'groupCoaching') && (
        <>
          {activeTab === 'all' ? (
            /* ALL tab: accordion container with collapse */
            <Box style={sectionContainerStyle}>
              <Group
                justify="space-between"
                style={getSectionHeaderStyle(sectionExpanded.groupCoaching)}
                onClick={() => toggleSection('groupCoaching')}
                wrap="nowrap"
              >
                <Group gap="sm">
                  <ThemeIcon color="gray" size={40} radius="md" variant="light">
                    <Users size={20} />
                  </ThemeIcon>
                  <div>
                    <Text fw={600} size="16px">
                      Group Coaching
                    </Text>
                    <Text size="13px" c="dimmed" mt={4}>
                      {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''}{' '}<span style={{ fontSize: 18, lineHeight: 1, verticalAlign: 'middle', padding: '0 2px' }}>·</span>{' '}
                      {sessionsPerGroup} session
                      {sessionsPerGroup !== 1 ? 's' : ''} each
                    </Text>
                  </div>
                  {/* MED-1038: read-only facilitation-config pills, grouped with the title */}
                  <ModalityConfigPills modality={groupCoachingModality} />
                </Group>
                <Group gap="sm" onClick={(e) => e.stopPropagation()}>
                  {/* MED-1350: the count is the coach made general huddles in
                      the groups. Intro and midpoint check-ins are 1:1 rows now,
                      so hide the toggle when no huddle exists. */}
                  {checkInCount > 0 && (
                    <Group gap={6}>
                      <Text size="xs" c="dimmed">
                        Show huddles
                      </Text>
                      <Badge size="sm" variant="light" color="gray" circle>
                        {checkInCount}
                      </Badge>
                      <Switch
                        size="sm"
                        checked={showOneOnOnes}
                        onChange={(e) => onSetShowOneOnOnes(e.currentTarget.checked)}
                      />
                    </Group>
                  )}
                  <Button
                    variant="default"
                    size="xs"
                    onClick={allGroupsExpanded ? onCollapseAllGroups : onExpandAllGroups}
                  >
                    {allGroupsExpanded ? 'Collapse All' : 'Expand All'}
                  </Button>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSection('groupCoaching');
                    }}
                  >
                    {sectionExpanded.groupCoaching ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </ActionIcon>
                </Group>
              </Group>
              <Collapse in={sectionExpanded.groupCoaching}>
                {/* Finalize banner inside All tab group coaching section */}
                {finalizationStatus?.ready && finalizationStatus.unfinalized_groups.length > 0 && onFinalizeAll && (
                  <Alert
                    icon={<CheckCircle size={18} color="#2D4739" />}
                    title="All session dates are set — ready to finalize and send invites"
                    styles={{
                      root: { backgroundColor: '#E8F5E9', borderRadius: 0, border: 'none', borderBottom: '1px solid #C8E6C9' },
                      body: { gap: 0 },
                      title: { color: '#2D4739', marginBottom: 0 },
                      icon: { color: '#2D4739' },
                      message: { color: '#2D4739', marginTop: 0 },
                    }}
                  >
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Text size="sm" c="#2D4739">
                        {finalizationStatus.total_sessions} sessions across{' '}
                        {finalizationStatus.unfinalized_groups.length} group
                        {finalizationStatus.unfinalized_groups.length !== 1 ? 's' : ''}.
                        {finalizationStatus.date_range.start && finalizationStatus.date_range.end && (
                          <>
                            {' '}
                            {formatDate(finalizationStatus.date_range.start)} -{' '}
                            {formatDate(finalizationStatus.date_range.end)}.
                          </>
                        )}{' '}
                        Review schedules and send calendar invitations.
                      </Text>
                      <Button
                        leftSection={<Send size={14} />}
                        onClick={onFinalizeAll}
                        size="sm"
                      >
                        Finalize & Send Invites
                      </Button>
                    </Group>
                  </Alert>
                )}
                <Box style={{ backgroundColor: '#FFFFFF' }}>
                  {groupingMode === 'session' ? (
                    <Stack gap="md" p="md">
                      <Group justify="flex-end">
                        <Button
                          variant="default"
                          size="xs"
                          leftSection={<Plus size={12} />}
                          onClick={onOpenBulkTopicEditor}
                        >
                          Set All Topics
                        </Button>
                      </Group>
                      <GroupCoachingBySessionView
                        cohortId={cohort.id}
                        huddles={cohortHuddles}
                        totalParticipants={totalParticipantsForCheckins}
                        onSessionClick={onOpenSessionById}
                        onHuddleClick={onHuddleClick}
                        refreshKey={bySessionRefreshKey}
                        modalityLiveSessionDeliveryEnabled={groupCoachingLiveSessionDeliveryEnabled}
                      />
                    </Stack>
                  ) : filteredGroups.length > 0 ? (
                    <Stack gap={0}>
                      {filteredGroups.map((group) => (
                        <SessionGroupCard
                          key={group.id}
                          group={group}
                          cohortId={cohort.id}

                          isExpanded={expandedGroups.has(group.id)}
                          onToggle={onToggleGroup}
                          onSessionClick={onSessionClick}
                          onEditSession={onEditSession}
                          onOpenTopicModal={onOpenTopicModal}
                          onFinalizeGroup={onFinalizeGroup}
                          cohortHuddles={cohortHuddles}
                          showOneOnOnes={showOneOnOnes}
                          onOpenSurveyResults={onOpenSurveyResults}
                          onOpenCoachFeedback={isPeerLedCohort ? undefined : onOpenCoachFeedback}
                          onParticipantClick={onParticipantClick}
                          onOpenFollowUps={onOpenFollowUps}
                          onAddTimeSlot={!group.scheduledTimeSlot && onAddTimeSlot ? () => onAddTimeSlot(group.id) : undefined}
                          modalityLiveSessionDeliveryEnabled={groupCoachingLiveSessionDeliveryEnabled}
                          showBookingLinks={!isPeerLedCohort}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Text c="dimmed" ta="center" py="xl">
                      No groups match the selected filter
                    </Text>
                  )}
                </Box>
              </Collapse>
            </Box>
          ) : (
            /* Group Coaching tab: plain header, no accordion */
            <Box>
              <Group justify="space-between" py="sm" wrap="nowrap">
                <Group gap="sm">
                  <ThemeIcon color="gray" size={40} radius="md" variant="light">
                    <Users size={20} />
                  </ThemeIcon>
                  <div>
                    <Text fw={600} size="16px">
                      Group Coaching
                    </Text>
                    <Text size="13px" c="dimmed" mt={4}>
                      {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''}{' '}<span style={{ fontSize: 18, lineHeight: 1, verticalAlign: 'middle', padding: '0 2px' }}>·</span>{' '}
                      {sessionsPerGroup} session
                      {sessionsPerGroup !== 1 ? 's' : ''} each
                    </Text>
                  </div>
                  {/* MED-1038: read-only facilitation-config pills, grouped with the title */}
                  <ModalityConfigPills modality={groupCoachingModality} />
                </Group>
                <Group gap="sm">
                  {/* MED-1350: the count is the coach made general huddles in
                      the groups. Intro and midpoint check-ins are 1:1 rows now,
                      so hide the toggle when no huddle exists. */}
                  {checkInCount > 0 && (
                    <Group gap={6}>
                      <Text size="xs" c="dimmed">
                        Show huddles
                      </Text>
                      <Badge size="sm" variant="light" color="gray" circle>
                        {checkInCount}
                      </Badge>
                      <Switch
                        size="sm"
                        checked={showOneOnOnes}
                        onChange={(e) => onSetShowOneOnOnes(e.currentTarget.checked)}
                      />
                    </Group>
                  )}
                  <Button
                    variant="default"
                    size="xs"
                    onClick={allGroupsExpanded ? onCollapseAllGroups : onExpandAllGroups}
                  >
                    {allGroupsExpanded ? 'Collapse All' : 'Expand All'}
                  </Button>
                </Group>
              </Group>
              {groupingMode === 'session' ? (
                <Stack gap="md">
                  <Group justify="flex-end">
                    <Button
                      variant="default"
                      size="xs"
                      leftSection={<Plus size={12} />}
                      onClick={onOpenBulkTopicEditor}
                    >
                      Set All Topics
                    </Button>
                  </Group>
                  <GroupCoachingBySessionView
                    cohortId={cohort.id}
                    huddles={cohortHuddles}
                    totalParticipants={totalParticipantsForCheckins}
                    onSessionClick={onOpenSessionById}
                    onHuddleClick={onHuddleClick}
                    refreshKey={bySessionRefreshKey}
                    modalityLiveSessionDeliveryEnabled={groupCoachingLiveSessionDeliveryEnabled}
                  />
                </Stack>
              ) : filteredGroups.length > 0 ? (
                <Stack gap="md">
                  {filteredGroups.map((group) => (
                    <SessionGroupCard
                      key={group.id}
                      group={group}
                      cohortId={cohort.id}
                      variant="card"
                      isExpanded={expandedGroups.has(group.id)}
                      onToggle={onToggleGroup}
                      onSessionClick={onSessionClick}
                      onEditSession={onEditSession}
                      onOpenTopicModal={onOpenTopicModal}
                      onFinalizeGroup={onFinalizeGroup}
                      cohortHuddles={cohortHuddles}
                      showOneOnOnes={showOneOnOnes}
                      onOpenSurveyResults={onOpenSurveyResults}
                      onParticipantClick={onParticipantClick}
                      onOpenFollowUps={onOpenFollowUps}
                      onAddTimeSlot={!group.scheduledTimeSlot && onAddTimeSlot ? () => onAddTimeSlot(group.id) : undefined}
                      modalityLiveSessionDeliveryEnabled={groupCoachingLiveSessionDeliveryEnabled}
                      showBookingLinks={!isPeerLedCohort}
                    />
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  No groups match the selected filter
                </Text>
              )}
            </Box>
          )}
        </>
      )}

      {/* 1:1 Coaching */}
      {/* MED-1350: check-ins show here even with no 1:1 modality and whatever the
          multi_modality flag says (Review Focus 1). */}
      {(activeTab === 'all' || activeTab === 'oneOnOne') &&
        showsOneOnOneSection(modalities, multiModalityEnabled) && (
        groupingMode === 'session' ? (
          <Box style={sectionContainerStyle}>
            <Group
              justify="space-between"
              style={{
                backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
                padding: '16px 20px',
                cursor: activeTab === 'all' ? 'pointer' : 'default',
                borderBottom: '1px solid #E5E2DC',
              }}
              onClick={activeTab === 'all' ? () => toggleSection('oneOnOne') : undefined}
              wrap="nowrap"
            >
              <Group gap="sm">
                <ThemeIcon color="orange" size={40} radius="md" variant="filled">
                  <User size={20} />
                </ThemeIcon>
                <div>
                  <Text fw={600} size="16px">
                    {/* MED-1350: a cohort with no 1:1 modality sells no 1:1s. */}
                    {modalities.hasOneOnOne ? '1:1 Coaching' : 'Check-ins'}
                  </Text>
                  <Text size="13px" c="dimmed" mt={4}>
                    {oneOnOneParticipants.length} participant{oneOnOneParticipants.length !== 1 ? 's' : ''}{' '}
                    <span style={{ fontSize: 18, lineHeight: 1, verticalAlign: 'middle', padding: '0 2px' }}>·</span>{' '}
                    {!modalities.hasOneOnOne ? (
                      'Check-ins'
                    ) : (
                      <>
                        {cohort.modalities?.find((m) => m.modality_type === 'one_on_one')?.session_count ?? 2} session
                        {(cohort.modalities?.find((m) => m.modality_type === 'one_on_one')?.session_count ?? 2) !== 1 ? 's' : ''} each
                      </>
                    )}
                  </Text>
                </div>
              </Group>
              {oneOnOneBookingLinks && (
                <Group gap="sm" wrap="wrap" justify="flex-end" ml="auto" maw="100%">
                  {oneOnOneBookingLinks}
                </Group>
              )}
              {activeTab === 'all' && (
                <ActionIcon variant="subtle" color="gray">
                  {sectionExpanded.oneOnOne ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </ActionIcon>
              )}
            </Group>
            {(activeTab !== 'all' || sectionExpanded.oneOnOne) && (
              <Box p="md" style={{ backgroundColor: '#FFFFFF' }}>
                <OneOnOneBySessionView
                  cohortId={cohort.id}
                  onSessionClick={onOpenSessionById}
                  refreshKey={bySessionRefreshKey}
                />
              </Box>
            )}
          </Box>
        ) : (
          <OneOnOneCoachingSection
            participants={oneOnOneParticipants}
            sessionCount={cohort.modalities?.find((m) => m.modality_type === 'one_on_one')?.session_count ?? 2}
            checkinsOnly={!modalities.hasOneOnOne}
            checkinCount={enabledCheckinCount(cohort)}
            useAccordion={activeTab === 'all'}
            isExpanded={activeTab === 'all' ? sectionExpanded.oneOnOne : true}
            onToggle={activeTab === 'all' ? () => toggleSection('oneOnOne') : undefined}
            onShowMore={activeTab === 'all' ? onNavigateToOneOnOne : undefined}
            // MED-1360: the full total in every tab, not the first loaded page.
            total={oneOnOneTotal}
            onLoadMore={activeTab === 'oneOnOne' ? onLoadMoreOneOnOne : undefined}
            loadingMore={activeTab === 'oneOnOne' ? loadingMoreOneOnOne : undefined}
            onAssignCoach={onAssignOneOnOneCoach}
            onSessionClick={onOneOnOneSessionClick}
            historyCohortId={cohort.id}
            viewMode={oneOnOneViewMode}
            onViewModeChange={onOneOnOneViewModeChange}
            headerActions={oneOnOneBookingLinks}
          />
        )
      )}
      {(activeTab === 'all' || activeTab === 'workshop') && modalities.hasWorkshop && multiModalityEnabled && (
        <WorkshopSessionsSection
          cohortId={cohort.id}
          workshops={workshops}
          pattern={workshopPattern}
          useAccordion={activeTab === 'all'}
          isExpanded={activeTab === 'all' ? sectionExpanded.workshop : true}
          onToggle={activeTab === 'all' ? () => toggleSection('workshop') : undefined}
          onWorkshopUpdated={onWorkshopUpdated}
          aiFeaturesEnabled={workshopAiFeaturesEnabled}
        />
      )}
    </Stack>
  );
}
